'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

import { SplitsLink as Link } from '~/components/Link'
import { AccountBanner } from '~/components/account-detail/AccountBanner'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { EARN_GUIDE_URL } from '~/constants/externalUrls'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgId } from '~/hooks/org'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import { getSplitsErrorMessage, useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { BackendTransactionCall } from '~/types'
import { captureException } from '~/utils/errors'

// Only the fields the server's migration builder actually sends.
type MigrationTransactionData = Pick<
  CreateTransactionData,
  'name' | 'smartAccountId' | 'chainId' | 'isOfframp' | 'types' | 'memo'
> & {
  calls: Array<Omit<BackendTransactionCall, 'value'> & { value: string }>
}

const useMigrateEarn = ({ onClose }: { onClose: () => void }) => {
  const orgId = useActiveOrgId()
  const { smartAccounts } = useSmartAccounts()
  const { serverGetRequest } = useServerRequests()
  const { createTransaction } = useCreateTransaction()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!orgId) return
      const response = await serverGetRequest<{
        transactionData: MigrationTransactionData
      }>({
        path: '/api/org/earn_migration',
        params: { orgId },
        onError: (error) => {
          captureException(error)
          toast.error(
            getSplitsErrorMessage(error) ?? 'Failed to build Earn migration',
          )
        },
      })
      if (!response) return

      const transactionData = response.data.transactionData
      // Trust the server builder to select the Treasury as the migration account.
      const treasury = smartAccounts.find(
        (account) => account.id === transactionData.smartAccountId,
      )
      if (!treasury) {
        const error = new Error(
          `Migration transaction account ${transactionData.smartAccountId} was not found`,
        )
        captureException(error)
        toast.error('Failed to find the migration account')
        return
      }
      onClose()
      await createTransaction({
        transactionId: undefined,
        dontOpenDialog: false,
        txData: {
          ...transactionData,
          smartAccountAddress: treasury.address,
          calls: transactionData.calls.map((call) => ({
            ...call,
            value: BigInt(call.value),
          })),
          // The balances provider lists the new vault token a few seconds
          // after the deposit lands, so the refresh fired on confirmation
          // misses the position. Refresh every account once more after the
          // provider has caught up.
          onConfirmation: () => {
            setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SMART_ACCOUNT_BALANCES],
              })
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ORG_BALANCES, orgId],
              })
            }, 3_000)
          },
        },
      })
    },
    // serverGetRequest handles its own errors; this catches everything after
    // it (malformed call values, createTransaction failures) so a failed
    // migration never dies silently.
    onError: (error) => {
      captureException(error)
      toast.error('Failed to start the Earn migration')
    },
  })
}

const EarnMigrationDialog = ({ onClose }: { onClose: () => void }) => {
  const { mutate: migrate, isPending: isMigrating } = useMigrateEarn({
    onClose,
  })

  return (
    <SplitsDialogContent
      id="earnMigration"
      variant="cta"
      title="Upgrade to Earn 2.0"
      actionButton={{
        text: 'Review transaction',
        onClick: () => migrate(),
        isDisabled: isMigrating,
      }}
      cancelButton={{ onClick: onClose }}
    >
      <p>
        Move your workspace’s existing Earn balances to the new vault and update
        your auto-earn automations in one transaction. Nothing moves until the
        transaction is approved.{' '}
        <Link path={EARN_GUIDE_URL} target="tab" underline>
          Learn more
        </Link>
      </p>
    </SplitsDialogContent>
  )
}

export const EarnMigrationBanner = () => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()

  return (
    <AccountBanner
      icon={<ArrowRight className="h-6 w-6 text-white" />}
      title="Upgrade to Earn 2.0"
      description="Move existing balances to the new vault"
      onClick={() =>
        setGlobalModalState({
          isOpen: true,
          content: <EarnMigrationDialog onClose={closeGlobalModal} />,
        })
      }
    />
  )
}
